import { getEnv } from "@/common/util/environment"
import { useProviderStore } from "@/modules/provide/create-offer/store/useProviderStore"

// =====================================================================
// Enums & Types
// =====================================================================

enum PRODUCT_FLAVOUR_ENUM {
  AUTOMOTIVE = "AUTOMOTIVE",
  NON_AUTOMOTIVE = "NON_AUTOMOTIVE"
}

enum PRODUCT_TYPE_ENUM {
  ESSENTIAL = "ESSENTIAL",
  PREMIUM = "PREMIUM",
  ENTERPRISE = "ENTERPRISE"
}

export enum DGK_FEATURES_ENUM {
  ASSETS = "assets",
  POLICIES = "policies",
  PUBLICATION_TEMPLATES = "publication_templates",
  CONNECTORS_MANAGEMENT = "connectors_management",
  OFFER = "offer",
  CONTRACTS_MANAGEMENT = "contracts_management",
  AGREEMENTS_MANAGEMENT = "agreements_management",
  DOWNLOAD_HISTORY = "download_history"
}

export enum DIGITAL_TWIN_FEATURES_ENUM {
  CSV_FILE_UPLOAD = "csv_file_upload",
  BROWSE = "browse",
  USE_CASES = "use_cases"
}

export enum CX_CORE_FEATURES_ENUM {
  CERTIFICATE_MANAGEMENT = "certificate_management",
  JOBS = "jobs",
  MESSAGING = "messaging",
  PCF_EXCHANGE = "pcf_exchange",
  CYBERSECURITY_KPIS = "cybersecurity_kpis",
  BLOCK_NOTIFICATIONS = "block_notifications",
  TRACEABILITY = "traceability",
  ELECTRONIC_CONTROL_UNIT = "electronic_control_unit"
}

export enum USE_CASES_ENUM {
  CERTIFICATE_MANAGEMENT = "companyCertificates",
  PCF = "co2",
  ECU = "ecu",
  ECU_TEST = "ecu-test",
  DCM = "dcm",
  CE = "ce",
  BATTERY_PASS_BMW = "batteryPassBmw",
  CYBER_SECURITY_KPI = "cyberSecurityKpi",
  TRACEABILITY = "traceability",
  BLOCKING_NOTIFICATIONS = "blockingNotifications",
  MODULAR_PRODUCTION = "modularProduction",
  QUALITY_MANAGEMENT = "qualityManagement",
  REAL_TIME_CONTROL = "realTimeControl"
}

export enum OTHER_PREMIUM_FEATURES {
  SWAGGER = "swagger"
}

export type Features =
  | CX_CORE_FEATURES_ENUM
  | DIGITAL_TWIN_FEATURES_ENUM
  | DGK_FEATURES_ENUM
  | OTHER_PREMIUM_FEATURES

// =====================================================================
// Feature group arrays
// =====================================================================

const DGK: Features[] = [
  DGK_FEATURES_ENUM.ASSETS,
  DGK_FEATURES_ENUM.POLICIES,
  DGK_FEATURES_ENUM.PUBLICATION_TEMPLATES,
  DGK_FEATURES_ENUM.CONNECTORS_MANAGEMENT,
  DGK_FEATURES_ENUM.OFFER,
  DGK_FEATURES_ENUM.CONTRACTS_MANAGEMENT,
  DGK_FEATURES_ENUM.AGREEMENTS_MANAGEMENT,
  DGK_FEATURES_ENUM.DOWNLOAD_HISTORY
]

const DT: Features[] = [
  DIGITAL_TWIN_FEATURES_ENUM.CSV_FILE_UPLOAD,
  DIGITAL_TWIN_FEATURES_ENUM.BROWSE,
  DIGITAL_TWIN_FEATURES_ENUM.USE_CASES
]

const CX_CORE: Features[] = [
  CX_CORE_FEATURES_ENUM.CERTIFICATE_MANAGEMENT,
  CX_CORE_FEATURES_ENUM.JOBS,
  CX_CORE_FEATURES_ENUM.MESSAGING,
  CX_CORE_FEATURES_ENUM.PCF_EXCHANGE,
  CX_CORE_FEATURES_ENUM.CYBERSECURITY_KPIS,
  CX_CORE_FEATURES_ENUM.BLOCK_NOTIFICATIONS,
  CX_CORE_FEATURES_ENUM.TRACEABILITY,
  CX_CORE_FEATURES_ENUM.ELECTRONIC_CONTROL_UNIT
]

const OTHER_PREMIUM: Features[] = [OTHER_PREMIUM_FEATURES.SWAGGER]

// =====================================================================
// Configuration mappings
// =====================================================================

const FeatureMapping: Record<string, Record<string, Features[][]>> = {
  [PRODUCT_TYPE_ENUM.ESSENTIAL]: {
    [PRODUCT_FLAVOUR_ENUM.NON_AUTOMOTIVE]: [DGK],
    [PRODUCT_FLAVOUR_ENUM.AUTOMOTIVE]: [DGK, DT, CX_CORE, OTHER_PREMIUM]
  },
  [PRODUCT_TYPE_ENUM.PREMIUM]: {
    [PRODUCT_FLAVOUR_ENUM.NON_AUTOMOTIVE]: [DGK],
    [PRODUCT_FLAVOUR_ENUM.AUTOMOTIVE]: [DGK, DT, CX_CORE, OTHER_PREMIUM]
  },
  [PRODUCT_TYPE_ENUM.ENTERPRISE]: {
    [PRODUCT_FLAVOUR_ENUM.NON_AUTOMOTIVE]: [DGK],
    [PRODUCT_FLAVOUR_ENUM.AUTOMOTIVE]: [DGK, DT, CX_CORE, OTHER_PREMIUM]
  }
}

const USE_CASES_FEATURE_MAPPING: Record<string, Features[]> = {
  [USE_CASES_ENUM.CERTIFICATE_MANAGEMENT]: [
    CX_CORE_FEATURES_ENUM.CERTIFICATE_MANAGEMENT,
    CX_CORE_FEATURES_ENUM.JOBS,
    DIGITAL_TWIN_FEATURES_ENUM.BROWSE,
    DIGITAL_TWIN_FEATURES_ENUM.CSV_FILE_UPLOAD,
    DIGITAL_TWIN_FEATURES_ENUM.USE_CASES,
    CX_CORE_FEATURES_ENUM.MESSAGING
  ],
  [USE_CASES_ENUM.PCF]: [
    CX_CORE_FEATURES_ENUM.PCF_EXCHANGE,
    CX_CORE_FEATURES_ENUM.JOBS,
    DIGITAL_TWIN_FEATURES_ENUM.BROWSE,
    DIGITAL_TWIN_FEATURES_ENUM.CSV_FILE_UPLOAD,
    DIGITAL_TWIN_FEATURES_ENUM.USE_CASES
  ],
  [USE_CASES_ENUM.BLOCKING_NOTIFICATIONS]: [
    CX_CORE_FEATURES_ENUM.BLOCK_NOTIFICATIONS,
    CX_CORE_FEATURES_ENUM.JOBS,
    CX_CORE_FEATURES_ENUM.MESSAGING
  ],
  [USE_CASES_ENUM.TRACEABILITY]: [
    CX_CORE_FEATURES_ENUM.TRACEABILITY,
    CX_CORE_FEATURES_ENUM.JOBS,
    DIGITAL_TWIN_FEATURES_ENUM.BROWSE,
    DIGITAL_TWIN_FEATURES_ENUM.CSV_FILE_UPLOAD,
    DIGITAL_TWIN_FEATURES_ENUM.USE_CASES,
    CX_CORE_FEATURES_ENUM.MESSAGING
  ],
  [USE_CASES_ENUM.ECU]: [
    CX_CORE_FEATURES_ENUM.ELECTRONIC_CONTROL_UNIT,
    CX_CORE_FEATURES_ENUM.JOBS,
    CX_CORE_FEATURES_ENUM.MESSAGING
  ],
  [USE_CASES_ENUM.ECU_TEST]: [
    CX_CORE_FEATURES_ENUM.ELECTRONIC_CONTROL_UNIT,
    CX_CORE_FEATURES_ENUM.JOBS,
    CX_CORE_FEATURES_ENUM.MESSAGING
  ],
  [USE_CASES_ENUM.CYBER_SECURITY_KPI]: [
    CX_CORE_FEATURES_ENUM.CYBERSECURITY_KPIS,
    CX_CORE_FEATURES_ENUM.JOBS,
    CX_CORE_FEATURES_ENUM.MESSAGING
  ],
  [USE_CASES_ENUM.CE]: [
    CX_CORE_FEATURES_ENUM.JOBS,
    CX_CORE_FEATURES_ENUM.MESSAGING,
    DIGITAL_TWIN_FEATURES_ENUM.BROWSE,
    DIGITAL_TWIN_FEATURES_ENUM.CSV_FILE_UPLOAD,
    DIGITAL_TWIN_FEATURES_ENUM.USE_CASES
  ],
  [USE_CASES_ENUM.BATTERY_PASS_BMW]: [
    CX_CORE_FEATURES_ENUM.JOBS,
    CX_CORE_FEATURES_ENUM.MESSAGING,
    DIGITAL_TWIN_FEATURES_ENUM.BROWSE,
    DIGITAL_TWIN_FEATURES_ENUM.USE_CASES
  ]
}

// Pre-computed reverse lookup: feature -> related use cases
const featureToUseCasesMap = new Map<string, string[]>()
for (const [useCase, features] of Object.entries(USE_CASES_FEATURE_MAPPING)) {
  for (const feature of features) {
    const existing = featureToUseCasesMap.get(feature) || []
    existing.push(useCase)
    featureToUseCasesMap.set(feature, existing)
  }
}

// =====================================================================
// Resolved state (populated once at app startup)
// =====================================================================

let resolvedFeatureSet: Set<Features> | null = null

// =====================================================================
// Public API
// =====================================================================

/**
 * Call once at app startup (after auth, before app.mount).
 * Fetches active use cases and builds the resolved feature set.
 */
export async function initFeatures(): Promise<void> {
  const providerStore = useProviderStore()
  await providerStore.fetchUsecases()

  const productFlavour = getEnv("VITE_PRODUCT_FLAVOUR") as string
  const productType = getEnv("VITE_PRODUCT_TYPE") as string
  const productFeatures = FeatureMapping[productType]?.[productFlavour]

  if (!productFeatures) {
    resolvedFeatureSet = new Set()
    return
  }

  resolvedFeatureSet = buildResolvedFeatureSet(
    productFeatures.flat(),
    providerStore.activeUseCaseIds
  )
}

/**
 * Synchronous feature check. Must be called after initFeatures() has completed.
 */
export function featureCheck(feature: Features): boolean {
  if (!resolvedFeatureSet) {
    console.warn("featureCheck called before initFeatures(). Returning false.")
    return false
  }
  return resolvedFeatureSet.has(feature)
}

// =====================================================================
// Internal helpers
// =====================================================================

function buildResolvedFeatureSet(
  availableFeatures: Features[],
  activeUseCases: string[]
): Set<Features> {
  const activeSet = new Set(activeUseCases)
  const resolved = new Set<Features>()

  for (const feature of availableFeatures) {
    const relatedUseCases = featureToUseCasesMap.get(feature)
    if (!relatedUseCases) {
      resolved.add(feature)
      continue
    }
    if (relatedUseCases.some((uc) => activeSet.has(uc))) {
      resolved.add(feature)
    }
  }

  return resolved
}
